// src/components/issues/ConnectedFromOtherBoardsRtk.tsx
import type { ColDef } from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { Button } from "reactstrap";
import {
  GetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsApiResponse,
  useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery,
} from "../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { thunkOpenIssueModalById } from "../../app/routes/issuesApp/issueSlice";
import { useAppDispatch } from "../../hooks";

export const ConnectedFromOtherBoardsRtk: React.FC<{ issueId: string; projectId: string }> = ({
  issueId,
  projectId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { data, isFetching, isError } = useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery({
    issueId,
    projectId,
  });

  const dispatch = useAppDispatch();

  // Kolumndefinitioner
  const columnDefs = useMemo<
    ColDef<GetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsApiResponse[number]>[]
  >(
    () => [
      { headerName: "ID", field: "id", width: 110, sortable: true },
      {
        headerName: "Tittel",
        field: "title",
        flex: 1,
        minWidth: 240,
        sortable: true,
        filter: true,
        cellRenderer: (p: any) => {
          const id = p?.data?._id as string | undefined;
          const title = p?.value ?? "";
          if (!id) return title;
          return (
            <Button
              color="link"
              className="p-0"
              onClick={() => {
                dispatch(thunkOpenIssueModalById(id));
              }}
            >
              {title}
            </Button>
          );
        },
      },
      {
        headerName: "Register",
        valueGetter: (p) => p.data?.issueBoard?.name ?? "",
        width: 240,
        sortable: true,
        filter: true,
      },
    ],
    [dispatch],
  );

  // Early returns efter hooks
  if (isError) return null;

  const rows = data ?? [];
  const count = rows.length;
  // Hide only when there are no connections on initial load, keep showing while refetching
  if (isFetching && count === 0) return null;
  if (count === 0) return null;

  const label = count === 1 ? "Også koblet til én annen sak" : `Også koblet til ${count} andre saker`;

  return (
    <div>
      <button
        type="button"
        className="btn btn-link p-0 align-baseline"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span style={{ marginRight: 6 }}>{expanded ? "▾" : "▸"}</span>
        {label}
      </button>

      {expanded && count > 0 && (
        <div className="ag-theme-quartz" style={{ width: "100%", marginTop: 8 }}>
          <AgGridReact<GetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsApiResponse[number]>
            rowData={rows}
            domLayout="autoHeight"
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
            localeText={AG_GRID_LOCALE_NOR}
            suppressMenuHide
            animateRows
          />
        </div>
      )}
    </div>
  );
};
